import { useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { usePageTitle } from '../../hooks/usePageTitle'
import { leadSourcesApi } from '../../api/settings'
import { organizationsApi, type Organization } from '../../api/contacts'
import { PageSpinner } from '../../components/ui/Spinner'
import { Modal } from '../../components/ui/Modal'
import { ConfirmDialog } from '../../components/ui/ConfirmDialog'
import { Plus, Pencil, Trash2, RefreshCw, AlertTriangle, Copy, Eye, EyeOff, ChevronDown, ChevronRight } from 'lucide-react'
import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { useAuthStore } from '../../store/auth'
import { DropdownSelect } from '../../components/ui/DropdownSelect'

interface LeadSource {
  id: string; name: string; platform: string; isActive: boolean
  webhookSecret: string; lastSyncAt: string | null; assignedUserId: string | null
  organizationId?: string | null
  accessToken?: string; appSecret?: string; pageId?: string; formId?: string
  fieldMapping?: Record<string, string>
  customWebhookToken?: string | null
}

async function copyToClipboard(value: string, label: string) {
  try {
    await navigator.clipboard.writeText(value)
    toast.success(`${label} copied`)
  } catch {
    toast.error('Copy failed — your browser may block clipboard access on non-HTTPS pages')
  }
}

function truncateToken(token: string): string {
  if (token.length <= 16) return token
  return `${token.slice(0, 6)}......${token.slice(-8)}`
}

function generateHex(bytes: number) {
  const arr = new Uint8Array(bytes)
  crypto.getRandomValues(arr)
  return Array.from(arr).map(b => b.toString(16).padStart(2, '0')).join('')
}

export function LeadSourcesSettings() {
  usePageTitle('Lead Sources')
  const { companyId } = useParams<{ companyId: string }>()
  const qc = useQueryClient()
  const [showCreate, setShowCreate] = useState(false)
  const [editTarget, setEditTarget] = useState<LeadSource | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; label: string } | null>(null)

  const { data: webhookConfig } = useQuery({
    queryKey: ['lead-sources-webhook-config', companyId],
    queryFn: () => leadSourcesApi.webhookConfig(companyId!).then(r => r.data),
    enabled: !!companyId,
  })

  const { data, isLoading } = useQuery({
    queryKey: ['lead-sources', companyId],
    queryFn: () => leadSourcesApi.list(companyId!).then(r => r.data),
    enabled: !!companyId,
  })

  const { data: orgs } = useQuery({
    queryKey: ['organizations', companyId],
    queryFn: () => organizationsApi.list(companyId!, { limit: 500 }).then(r => r.data.data),
    enabled: !!companyId,
  })
  const orgMap = new Map((orgs ?? []).map((o: Organization) => [o.id, o.name]))

  const deleteMutation = useMutation({
    mutationFn: (id: string) => leadSourcesApi.remove(companyId!, id),
    onSuccess: () => {
      toast.success('Lead source deleted')
      qc.invalidateQueries({ queryKey: ['lead-sources', companyId] })
      setDeleteTarget(null)
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.message
      toast.error(typeof msg === 'string' ? msg : 'Failed to delete')
    },
  })

  const syncMutation = useMutation({
    mutationFn: (id: string) => leadSourcesApi.syncNow(companyId!, id),
    onSuccess: () => toast.success('Sync queued'),
    onError: (err: any) => {
      const msg = err?.response?.data?.message
      toast.error(typeof msg === 'string' ? msg : 'Failed to queue sync')
    },
  })

  if (isLoading) return <PageSpinner />

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold text-gray-900">Lead Sources</h2>
        <button onClick={() => setShowCreate(true)} className="btn btn-primary btn-sm">
          <Plus className="w-3.5 h-3.5" /> Add Source
        </button>
      </div>

      {webhookConfig && (
        <div className="card p-4 space-y-3 bg-blue-50 border border-blue-100">
          <div className="flex items-center gap-2 text-blue-700 font-medium text-sm">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
            Company-wide webhook — paste these into your Meta App Dashboard
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <div className="text-xs text-gray-600 mb-1 font-medium">Callback URL</div>
              <div className="flex items-center gap-1">
                <code className="flex-1 bg-white border border-gray-200 px-2 py-1.5 rounded text-xs break-all">
                  {webhookConfig.callbackUrl}
                </code>
                <button onClick={() => copyToClipboard(webhookConfig.callbackUrl, 'Callback URL')} className="btn btn-ghost btn-sm p-1.5 text-gray-600 hover:text-gray-900 flex-shrink-0" title="Copy"><Copy className="w-3.5 h-3.5" /></button>
              </div>
            </div>
            <div>
              <div className="text-xs text-gray-600 mb-1 font-medium">Verify Token</div>
              <div className="flex items-center gap-1">
                <code className="flex-1 bg-white border border-gray-200 px-2 py-1.5 rounded text-xs">
                  {truncateToken(webhookConfig.verifyToken)}
                </code>
                <button onClick={() => copyToClipboard(webhookConfig.verifyToken, 'Verify Token')} className="btn btn-ghost btn-sm p-1.5 text-gray-600 hover:text-gray-900 flex-shrink-0" title="Copy full token"><Copy className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          </div>
          <p className="text-xs text-gray-500">All sources without a custom override use this URL and token. Routes by Form ID.</p>
        </div>
      )}

      <div className="card overflow-hidden">
        <table className="table">
          <thead>
            <tr><th>Name</th><th>Platform</th><th>Campaign</th><th>Webhook</th><th>Status</th><th>Last Sync</th><th></th></tr>
          </thead>
          <tbody>
            {(!data || data.length === 0) && (
              <tr><td colSpan={7} className="text-center py-8 text-gray-500 text-sm">No lead sources configured</td></tr>
            )}
            {data?.map((s: LeadSource) => (
              <tr key={s.id}>
                <td className="font-medium">{s.name}</td>
                <td><span className="badge badge-purple capitalize">{s.platform}</span></td>
                <td>
                  {s.organizationId && orgMap.has(s.organizationId)
                    ? <span className="badge badge-purple text-xs">{orgMap.get(s.organizationId)}</span>
                    : <span className="text-gray-400 text-xs">—</span>}
                </td>
                <td>
                  {s.customWebhookToken
                    ? <span className="badge badge-yellow text-xs">custom</span>
                    : <span className="text-gray-400 text-xs">company default</span>}
                </td>
                <td><span className={`badge ${s.isActive ? 'badge-green' : 'badge-gray'}`}>{s.isActive ? 'Active' : 'Inactive'}</span></td>
                <td className="text-gray-500 text-xs">{s.lastSyncAt ? new Date(s.lastSyncAt).toLocaleString() : '—'}</td>
                <td className="flex items-center gap-1">
                  <button onClick={() => syncMutation.mutate(s.id)} className="btn btn-ghost btn-sm p-1 text-blue-600" title="Sync now"><RefreshCw className="w-3.5 h-3.5" /></button>
                  <button onClick={() => setEditTarget(s)} className="btn btn-ghost btn-sm p-1 text-gray-600" title="Edit"><Pencil className="w-3.5 h-3.5" /></button>
                  <button onClick={() => setDeleteTarget({ id: s.id, label: s.name })} className="btn btn-ghost btn-sm p-1 text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal
        open={showCreate || !!editTarget}
        onClose={() => { setShowCreate(false); setEditTarget(null) }}
        title={editTarget ? 'Edit Lead Source' : 'Add Lead Source'}
        size="2xl"
      >
        <SourceForm
          companyId={companyId!}
          existing={editTarget}
          orgs={orgs ?? []}
          companyCallbackUrl={webhookConfig?.callbackUrl}
          companyVerifyToken={webhookConfig?.verifyToken}
          onSuccess={() => { setShowCreate(false); setEditTarget(null); qc.invalidateQueries({ queryKey: ['lead-sources', companyId] }) }}
        />
      </Modal>

      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
        title="Delete lead source?"
        message={`This will remove "${deleteTarget?.label}". The webhook URL stops accepting deliveries; existing leads from this source are kept.`}
        confirmLabel="Delete"
        isPending={deleteMutation.isPending}
      />
    </div>
  )
}

function SourceForm({ companyId, existing, orgs, companyCallbackUrl, companyVerifyToken, onSuccess }: {
  companyId: string; existing: LeadSource | null; orgs: Organization[]
  companyCallbackUrl?: string; companyVerifyToken?: string
  onSuccess: () => void
}) {
  const { user } = useAuthStore()
  const isAdmin = user?.role === 'administrator'
  const [showAccessToken, setShowAccessToken] = useState(false)
  const [showAppSecret, setShowAppSecret] = useState(false)
  const [overrideEnabled, setOverrideEnabled] = useState(!!existing?.customWebhookToken)
  const [overrideExpanded, setOverrideExpanded] = useState(!!existing?.customWebhookToken)
  const [customToken, setCustomToken] = useState(() => existing?.customWebhookToken ?? generateHex(16))
  const [customVerify, setCustomVerify] = useState(() =>
    existing?.customWebhookToken ? existing.webhookSecret : generateHex(20)
  )
  const [webhookConfirmAction, setWebhookConfirmAction] = useState<(() => void) | null>(null)

  function requireWebhookConfirm(action: () => void) {
    setWebhookConfirmAction(() => action)
  }

  const { register, handleSubmit, reset, watch, setValue } = useForm<{
    name: string; platform: string; organizationId?: string
    accessToken?: string; appSecret?: string; pageId?: string; formId?: string
    fieldMapping?: string; isActive?: boolean
  }>({
    defaultValues: existing
      ? {
          name: existing.name, platform: existing.platform,
          organizationId: existing.organizationId ?? '',
          accessToken: '', appSecret: '',
          pageId: existing.pageId, formId: existing.formId,
          fieldMapping: existing.fieldMapping ? JSON.stringify(existing.fieldMapping, null, 2) : '',
          isActive: existing.isActive,
        }
      : { platform: 'facebook', organizationId: '' },
  })
  const platform = watch('platform')

  useEffect(() => {
    if (!existing || !isAdmin) return
    let cancelled = false
    leadSourcesApi.revealSecrets(companyId, existing.id)
      .then(r => {
        if (cancelled) return
        if (r.data.accessToken) setValue('accessToken', r.data.accessToken)
        if (r.data.appSecret) setValue('appSecret', r.data.appSecret)
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [companyId, existing, isAdmin, setValue])

  const mutation = useMutation({
    mutationFn: (dto: any) => {
      const payload: Record<string, unknown> = {
        name: dto.name,
        pageId: dto.pageId || undefined,
        formId: dto.formId || undefined,
        fieldMapping: dto.fieldMapping ? JSON.parse(dto.fieldMapping) : {},
        organizationId: dto.organizationId || null,
      }
      if (dto.accessToken) payload.accessToken = dto.accessToken
      if (dto.appSecret) payload.appSecret = dto.appSecret
      if (overrideEnabled) {
        payload.customWebhookToken = customToken
        payload.webhookSecret = customVerify
      } else {
        payload.customWebhookToken = ''
      }
      if (existing) {
        payload.isActive = dto.isActive
        return leadSourcesApi.update(companyId, existing.id, payload)
      }
      payload.platform = dto.platform
      return leadSourcesApi.create(companyId, payload)
    },
    onSuccess: () => { reset(); toast.success(existing ? 'Lead source updated' : 'Lead source created'); onSuccess() },
    onError: (err: any) => {
      const msg = err?.response?.data?.message
      toast.error(typeof msg === 'string' ? msg : Array.isArray(msg) ? msg.join('. ') : 'Failed — check JSON in field mapping')
    },
  })

  const showMetaFields = platform === 'facebook' || platform === 'instagram'
  const fakeDots = '••••••••••••••••'
  const overrideCallbackUrl = `https://erpnext.comebrighter.com/webhooks/${customToken}/meta`

  return (
    <form onSubmit={handleSubmit(d => mutation.mutate(d))} className="space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="label">Name *</label>
          <input {...register('name', { required: true })} className="input" placeholder="e.g. Facebook Page — John" />
        </div>
        {existing ? (
          <div>
            <label className="label">Platform</label>
            <input value={existing.platform} disabled readOnly className="input" />
            <p className="text-xs text-gray-500 mt-1">Platform can't be changed after creation.</p>
          </div>
        ) : (
          <div>
            <label className="label">Platform</label>
            <DropdownSelect
              value={watch('platform') ?? 'facebook'}
              onChange={v => setValue('platform', v)}
              options={[
                { value: 'facebook', label: 'Facebook' },
                { value: 'instagram', label: 'Instagram' },
                { value: 'google', label: 'Google' },
                { value: 'tiktok', label: 'TikTok' },
                { value: 'generic_webhook', label: 'Generic Webhook' },
              ]}
            />
          </div>
        )}
        {showMetaFields && (
          <>
            <div>
              <label className="label">Access Token</label>
              {existing && !isAdmin ? (
                <input value={fakeDots} disabled readOnly className="input" />
              ) : (
                <div className="flex items-center gap-1">
                  <input {...register('accessToken')} type={showAccessToken ? 'text' : 'password'} className="input flex-1" placeholder={existing ? '' : 'Meta Page Access Token'} autoComplete="new-password" />
                  {isAdmin && (
                    <button type="button" onClick={() => setShowAccessToken(v => !v)} className="btn btn-ghost btn-sm p-2 text-gray-500">
                      {showAccessToken ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                    </button>
                  )}
                </div>
              )}
            </div>
            <div>
              <label className="label">App Secret</label>
              {existing && !isAdmin ? (
                <input value={fakeDots} disabled readOnly className="input" />
              ) : (
                <div className="flex items-center gap-1">
                  <input {...register('appSecret')} type={showAppSecret ? 'text' : 'password'} className="input flex-1" placeholder={existing ? '' : 'Meta App Secret (min 16 chars)'} autoComplete="new-password" />
                  {isAdmin && (
                    <button type="button" onClick={() => setShowAppSecret(v => !v)} className="btn btn-ghost btn-sm p-2 text-gray-500">
                      {showAppSecret ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                    </button>
                  )}
                </div>
              )}
              <p className="text-xs text-gray-500 mt-1">Required for live deliveries. Meta App Dashboard → Settings → Basic.</p>
            </div>
            <div>
              <label className="label">Page ID</label>
              <input {...register('pageId')} className="input" placeholder="Meta Page ID (numeric)" />
            </div>
            <div>
              <label className="label">Form ID *</label>
              <input {...register('formId', { required: true })} className="input" placeholder="Meta Lead Form ID" />
              <p className="text-xs text-gray-500 mt-1">Required — used to route webhook events to this source.</p>
            </div>
          </>
        )}
        {!showMetaFields && (
          <div>
            <label className="label">Form ID *</label>
            <input {...register('formId', { required: true })} className="input" placeholder="Form / Source ID" />
            <p className="text-xs text-gray-500 mt-1">Required — used as key to identify this source.</p>
          </div>
        )}
        <div className="md:col-span-2">
          <label className="label">Field Mapping (JSON)</label>
          <textarea {...register('fieldMapping')} rows={4} className="input font-mono text-xs resize-y"
            placeholder={'{\n  "full_name": "firstName",\n  "email": "email"\n}'} />
        </div>
        {orgs.length > 0 && (
          <div>
            <label className="label">Campaign / Organization</label>
            <DropdownSelect
              value={watch('organizationId') ?? ''}
              onChange={v => setValue('organizationId', v)}
              placeholder="— None —"
              options={[{ value: '', label: '— None —' }, ...orgs.map(o => ({ value: o.id, label: o.name }))]}
            />
          </div>
        )}
        {existing && (
          <label className="md:col-span-2 flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" {...register('isActive')} /> Source active
          </label>
        )}
      </div>

      {/* Webhook callback settings — always shown */}
      <div className="border border-gray-200 rounded">
        <button
          type="button"
          onClick={() => setOverrideExpanded(v => !v)}
          className="w-full flex items-center justify-between px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-50 rounded"
        >
          <span className="flex items-center gap-2">
            {overrideExpanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
            <span className="font-medium">Webhook callback settings</span>
            {overrideEnabled
              ? <span className="badge badge-yellow text-xs">custom</span>
              : <span className="badge badge-gray text-xs">company default</span>}
          </span>
        </button>

        {overrideExpanded && (
          <div className="px-3 pb-3 space-y-3 border-t border-gray-100 pt-3">
            {!overrideEnabled ? (
              <>
                <p className="text-xs text-gray-500">This source uses the company-wide webhook. Enable a custom override to assign a unique URL and verify token to this source only.</p>
                {(companyCallbackUrl || companyVerifyToken) && (
                  <div className="space-y-2">
                    {companyCallbackUrl && (
                      <div>
                        <div className="text-xs text-gray-600 mb-1 font-medium">Callback URL (company)</div>
                        <div className="flex items-center gap-1">
                          <code className="flex-1 bg-gray-50 border border-gray-200 px-2 py-1.5 rounded text-xs break-all text-gray-500">{companyCallbackUrl}</code>
                          <button type="button" onClick={() => copyToClipboard(companyCallbackUrl, 'Callback URL')} className="btn btn-ghost btn-sm p-1.5 text-gray-500 flex-shrink-0"><Copy className="w-3.5 h-3.5" /></button>
                        </div>
                      </div>
                    )}
                    {companyVerifyToken && (
                      <div>
                        <div className="text-xs text-gray-600 mb-1 font-medium">Verify Token (company)</div>
                        <div className="flex items-center gap-1">
                          <code className="flex-1 bg-gray-50 border border-gray-200 px-2 py-1.5 rounded text-xs text-gray-500">{truncateToken(companyVerifyToken)}</code>
                          <button type="button" onClick={() => copyToClipboard(companyVerifyToken, 'Verify Token')} className="btn btn-ghost btn-sm p-1.5 text-gray-500 flex-shrink-0"><Copy className="w-3.5 h-3.5" /></button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => requireWebhookConfirm(() => setOverrideEnabled(true))}
                  className="btn btn-sm btn-secondary"
                >
                  Enable custom webhook for this source
                </button>
              </>
            ) : (
              <>
                <p className="text-xs text-gray-500">This source has its own unique URL. Changing these values will break existing Facebook subscriptions using them.</p>
                <div className="space-y-2">
                  <div>
                    <div className="text-xs text-gray-600 mb-1 font-medium">Custom Callback URL</div>
                    <div className="flex items-center gap-1">
                      <code className="flex-1 bg-gray-50 border border-gray-200 px-2 py-1.5 rounded text-xs break-all">{overrideCallbackUrl}</code>
                      <button type="button" onClick={() => copyToClipboard(overrideCallbackUrl, 'Custom Callback URL')} className="btn btn-ghost btn-sm p-1.5 text-gray-600 flex-shrink-0"><Copy className="w-3.5 h-3.5" /></button>
                      <button type="button" onClick={() => requireWebhookConfirm(() => setCustomToken(generateHex(16)))} className="btn btn-ghost btn-sm p-1.5 text-gray-600 flex-shrink-0" title="Regenerate"><RefreshCw className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-600 mb-1 font-medium">Custom Verify Token</div>
                    <div className="flex items-center gap-1">
                      <code className="flex-1 bg-gray-50 border border-gray-200 px-2 py-1.5 rounded text-xs">{truncateToken(customVerify)}</code>
                      <button type="button" onClick={() => copyToClipboard(customVerify, 'Custom Verify Token')} className="btn btn-ghost btn-sm p-1.5 text-gray-600 flex-shrink-0"><Copy className="w-3.5 h-3.5" /></button>
                      <button type="button" onClick={() => requireWebhookConfirm(() => setCustomVerify(generateHex(20)))} className="btn btn-ghost btn-sm p-1.5 text-gray-600 flex-shrink-0" title="Regenerate"><RefreshCw className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => requireWebhookConfirm(() => setOverrideEnabled(false))}
                  className="btn btn-sm btn-secondary text-xs"
                >
                  Use company default callback settings
                </button>
              </>
            )}
          </div>
        )}
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <button type="submit" disabled={mutation.isPending} className="btn btn-primary btn-sm">
          {existing ? 'Save changes' : 'Create Source'}
        </button>
      </div>

      <ConfirmDialog
        isOpen={!!webhookConfirmAction}
        onClose={() => setWebhookConfirmAction(null)}
        onConfirm={() => { webhookConfirmAction?.(); setWebhookConfirmAction(null) }}
        title="Edit webhook callback details?"
        message="You are trying to edit webhook callback details. Any existing Facebook webhook subscriptions using the current URL or verify token will stop working until you update them. Are you sure?"
        confirmLabel="Yes, proceed"
      />
    </form>
  )
}
